import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { AdminId, CompanyId, SiteId } from '../common/ids';

import { SiteStatus } from './sites';

// ---------------------------------------------------------------------------
// Enums (data-model.md · admin_users)
// ---------------------------------------------------------------------------

export const AdminRole = z.enum(['SUPER_ADMIN', 'COMPANY_ADMIN', 'SITE_MANAGER', 'VIEWER']);
export type AdminRole = z.infer<typeof AdminRole>;

export const AdminStatus = z.enum(['ENABLED', 'DISABLED']);
export type AdminStatus = z.infer<typeof AdminStatus>;

/** Roles que un Company Admin puede crear desde la UI (`POST /admins`). */
export const CreatableAdminRole = z.enum(['COMPANY_ADMIN', 'SITE_MANAGER']);
export type CreatableAdminRole = z.infer<typeof CreatableAdminRole>;

// ---------------------------------------------------------------------------
// Entidad
// ---------------------------------------------------------------------------

/** Sede del alcance resuelta: `{ id, name, status }`. */
export const SiteScopeItemSchema = z.object({
  id: SiteId,
  name: z.string(),
  status: SiteStatus,
});
export type SiteScopeItem = z.infer<typeof SiteScopeItemSchema>;

export const AdminNameSchema = z.string().min(1).max(120);
export const AdminEmailSchema = z.email().max(254);

export const AdminUserSchema = z.object({
  id: AdminId,
  /** `null` solo para `SUPER_ADMIN`. */
  company_id: CompanyId.nullable(),
  email: AdminEmailSchema,
  name: AdminNameSchema,
  role: AdminRole,
  status: AdminStatus,
  /** Vacío para `COMPANY_ADMIN` (alcance implícito: toda la empresa). */
  site_scope: z.array(SiteScopeItemSchema),
  has_password: z.boolean(),
  last_login_at: NullableIsoDateTimeSchema,
  created_at: IsoDateTimeSchema,
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * `POST /admins`: `{ name, email, role, site_scope? }`.
 * `site_scope` es obligatorio y no vacío si `role = SITE_MANAGER`; se ignora si `COMPANY_ADMIN`.
 */
export const CreateAdminBodySchema = z
  .object({
    name: AdminNameSchema,
    email: AdminEmailSchema,
    role: CreatableAdminRole,
    site_scope: z.array(SiteId).optional(),
  })
  .refine((body) => body.role !== 'SITE_MANAGER' || (body.site_scope?.length ?? 0) > 0, {
    message: 'Un encargado de sede necesita al menos una sede en su alcance',
    path: ['site_scope'],
  });
export type CreateAdminBody = z.infer<typeof CreateAdminBodySchema>;

/** `PATCH /admins/{id}`: parcial de `{ name, site_scope }`. */
export const UpdateAdminBodySchema = z.object({
  name: AdminNameSchema.optional(),
  site_scope: z.array(SiteId).optional(),
});
export type UpdateAdminBody = z.infer<typeof UpdateAdminBodySchema>;

/** `GET /admins?role&status` */
export const AdminListQuerySchema = z.object({
  role: AdminRole.optional(),
  status: AdminStatus.optional(),
});
export type AdminListQuery = z.infer<typeof AdminListQuerySchema>;

/** Parámetro de ruta `{id}` de administrador. */
export const AdminIdParamsSchema = z.object({ id: AdminId });
export type AdminIdParams = z.infer<typeof AdminIdParamsSchema>;

/** Parámetros de `POST /companies/{id}/admins/{adminId}/setup-link`. */
export const CompanyAdminParamsSchema = z.object({ id: CompanyId, adminId: AdminId });
export type CompanyAdminParams = z.infer<typeof CompanyAdminParamsSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** `200 { items: AdminUser[] }` */
export const AdminListResponseSchema = z.object({
  items: z.array(AdminUserSchema),
});
export type AdminListResponse = z.infer<typeof AdminListResponseSchema>;

/** `201 { setup_link, setup_expires_at }` (nuevo token SETUP; supera el anterior). */
export const SetupLinkResponseSchema = z.object({
  setup_link: z.url(),
  setup_expires_at: IsoDateTimeSchema,
});
export type SetupLinkResponse = z.infer<typeof SetupLinkResponseSchema>;

/** `201 { admin, setup_link, setup_expires_at }` de `POST /admins`. */
export const CreateAdminResponseSchema = SetupLinkResponseSchema.extend({
  admin: AdminUserSchema,
});
export type CreateAdminResponse = z.infer<typeof CreateAdminResponseSchema>;
